/** @jsxImportSource @emotion/react */
import React, { ReactNode, useState, createContext, useContext } from "react";
import { createTourStore, TourStoreState } from "./TourProvider.helpers";
import AppLoading from "components/AppLoading/AppLoading";
import assert from "assert";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { isDefined } from "utils/isDefined";
import { useStore } from "zustand";
import { css, Global } from "@emotion/react";

import { TourAccessPersonalSettings } from "./tours/AccessPersonalSettings";
import { TourProjectSharing } from "./tours/ProjectSharing";
import { TourOrganizationManage } from "./tours/OrganizationManage";
import { TourOrganizationManageUsers } from "./tours/OrganizationManageUsers";
import { TourOrganizationManageUsage } from "./tours/OrganizationManageUsage";
import { TourNewVersionIntro_25_2 } from "./tours/NewVersionIntro_25_2";
import { TourDataTableUploadFiles } from "./tours/DataTableUploadFiles";
import { TourProjectSharingPS } from "./tours/ProjectSharingPS";

export const Tours = {
  TourNewVersionIntro_25_2,
  TourAccessPersonalSettings,
  TourProjectSharing,
  TourProjectSharingPS,
  TourDataTableUploadFiles,
  TourOrganizationManage,
  TourOrganizationManageUsers,
  TourOrganizationManageUsage,
};

export type TourKey = keyof typeof Tours;

const TourContext = createContext<TourStore | undefined>(undefined);

export type TourStore = Awaited<ReturnType<typeof createTourStore>>;

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const [store, setStore] = useState<TourStore>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  React.useEffect(() => {
    // Reset state
    setStore(undefined);
    setIsLoading(true);
    setError(undefined);
    // Create a new store
    createTourStore()
      .then(setStore)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, []);

  if (error !== undefined) {
    throw error;
  }

  if (isLoading) {
    return <AppLoading />;
  }
  return (
    <TourContext.Provider value={store}>
      {children}
      <Global
        styles={css`
          .euiTourStepIndicator svg.euiIcon[aria-label="complete"] circle {
            stroke: #646a77;
          }
          .euiTourStepIndicator svg.euiIcon[aria-label="active"] circle {
            stroke: #007871;
          }
          .euiTourStepIndicator svg.euiIcon[aria-label="incomplete"] circle {
            stroke: #007871;
            fill: white;
          }
          .euiPopoverTitle.euiTourHeader {
            margin-block-end: 0px;
            padding-bottom: 8px;
          }
        `}
      />
    </TourContext.Provider>
  );
};

/** Hook for consuming the {@link TourContext} */
export const useTourContext = <T,>(
  selector: (state: TourStoreState) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  const store = useContext(TourContext);
  assert(isDefined(store), new ContextOutOfBoundsError("TourContext"));
  return useStore(store, selector, equalityFn);
};
